import React, { useState, useEffect } from 'react';
import { Button, Card, Table, Modal, Form } from 'react-bootstrap';
import { BsListCheck, BsPencilSquare, BsTrash } from 'react-icons/bs';
import Topbar from './components/Topbar';
import SuperAdminSidebar from './components/SuperAdminSidebar';
import styles from '../../assets/css/DepartmentManagement.module.css';
import axios from 'axios';
import { toast } from 'react-toastify';

function DepartmentManagement() {
  const [departmentName, setDepartmentName] = useState('');
  const [departments, setDepartments] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  const [editedDeptName, setEditedDeptName] = useState('');

  const fetchDepartments = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warn('Authorization token not found. Please log in again.');
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}departmentlist.php?_=${new Date().getTime()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        const apiDepartments = response.data.departments.map((dept) => ({
          id: dept.id,
          name: dept.department_name,
        }));
        setDepartments(apiDepartments);
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while fetching departments.');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (departmentName.trim() === '') {
      toast.warn('Please enter a department name.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warn('Authorization token not found. Please log in again.');
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}adddepartment.php`,
        { department_name: departmentName.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.success) {
        setDepartmentName('');
        toast.success('Department created successfully!');
        fetchDepartments();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while creating the department.');
      console.error(error);
    }
  };

  const handleShowEditModal = (dept) => {
    setEditingDepartment(dept);
    setEditedDeptName(dept.name);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingDepartment(null);
    setEditedDeptName('');
  };

  const handleUpdateDepartment = async () => {
    if (editedDeptName.trim() === '') {
      toast.warn('Department name cannot be empty.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warn('Authorization token not found. Please log in again.');
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}editdepartment.php`,
        {
          department_id: editingDepartment.id,
          department_name: editedDeptName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.success) {
        setDepartments(departments.map(dept =>
          dept.id === editingDepartment.id ? { ...dept, name: editedDeptName.trim() } : dept
        ));
        toast.success('Department updated successfully!');
        handleCloseEditModal();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while updating the department.');
      console.error(error);
    }
  };

  const handleShowDeleteModal = (dept) => {
    setDeletingDepartment(dept);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingDepartment(null);
  };

  const handleDeleteDepartment = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warn('Authorization token not found. Please log in again.');
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}deletedepartment.php`,
        {
          department_id: deletingDepartment.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.success) {
        setDepartments(departments.filter(dept => dept.id !== deletingDepartment.id));
        toast.success('Department deleted successfully!');
        handleCloseDeleteModal();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while deleting the department.');
      console.error(error);
    }
  };

  return (
    <>
      <Topbar />
      <div className="d-flex" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <SuperAdminSidebar />
        <div className="flex-grow-1 p-3 p-md-4 bg-light">
          <header className="mb-4 d-flex justify-content-between align-items-center">
            <h1 className="h2">Department Management</h1>
          </header>

          {/* Create New Department Section */}
          <div className="card col-5 mb-4">
            <div className={styles.cardHeader}>
              <span className={styles.cardHeaderIcon}>+</span> Create New Department
            </div>
            <div className="card-body">
              <form onSubmit={handleCreateDepartment}>
                <div className="mb-3">
                  <label htmlFor="departmentName" className="form-label">
                    Department Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="departmentName"
                    placeholder="Enter department name"
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                  />
                </div>
                <button type="submit" className={`btn btn-primary ${styles.createButton}`}>
                  Create Department
                </button>
              </form>
            </div>
          </div>

          {/* Department List Section */}
          <div className="card">
            <Card.Header as="h5" className="d-flex align-items-center gap-2" style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
              <BsListCheck style={{ color: '#1976d2' }} /> <span style={{ color: '#1976d2' }}>Department List</span>
            </Card.Header>
            <div className="card-body">
              <Table striped bordered hover responsive="sm">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Department Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept, index) => (
                    <tr key={dept.id}>
                      <td>{index + 1}</td>
                      <td>{dept.name}</td>
                      <td className="d-flex gap-2">
                        <Button variant="warning" size="sm" onClick={() => handleShowEditModal(dept)}>
                          <BsPencilSquare />
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => handleShowDeleteModal(dept)}>
                          <BsTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Department Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formEditDepartmentName">
              <Form.Label>Department Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter department name"
                value={editedDeptName}
                onChange={(e) => setEditedDeptName(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleUpdateDepartment}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the department <strong>"{deletingDepartment?.name}"</strong> ? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteDepartment}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DepartmentManagement; 